import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useWorkspace } from "@/hooks/useWorkspace";

export interface KPIRange { from: Date; to: Date }

interface KPIData {
  totalClients: number;
  clientsChange: number;
  revenue: number;
  revenueChange: number;
  posRevenue: number;
  posChange: number;
  workshopRevenue: number;
  workshopChange: number;
  expenses: number;
  expensesChange: number;
  activeWorkOrders: number;
  conversionRate: number;
  conversionChange: number;
  loading: boolean;
}

const d = (x: Date) => {
  const y = x.getFullYear(), m = String(x.getMonth() + 1).padStart(2, "0"), dd = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const sum = (rows: any[] | null | undefined, f: (r: any) => number) => (rows || []).reduce((s, r) => s + (f(r) || 0), 0);
const pct = (a: number, b: number) => (b > 0 ? ((a - b) / b) * 100 : 0);

async function periodTotals(ws: string, from: string, to: string) {
  const [pos, posAll, wi, wo, exp, po, cl] = await Promise.all([
    supabase.from("invoices").select("total").eq("workspace_id", ws).eq("status", "paid").gte("issue_date", from).lte("issue_date", to).limit(10000),
    supabase.from("invoices").select("id", { count: "exact", head: true }).eq("workspace_id", ws).gte("issue_date", from).lte("issue_date", to),
    supabase.from("workshop_invoices").select("total").eq("workspace_id", ws).eq("status", "paid").gte("issue_date", from).lte("issue_date", to).limit(10000),
    supabase.from("work_orders").select("advance_cost, remaining_cost, workshop_invoices(status)").eq("workspace_id", ws).in("status", ["completed", "delivered"]).gte("actual_completion", from).lte("actual_completion", `${to}T23:59:59`).limit(10000),
    supabase.from("expenses").select("total_amount").eq("workspace_id", ws).gte("issue_date", from).lte("issue_date", to).limit(10000),
    supabase.from("orders").select("total, subtotal").eq("workspace_id", ws).eq("order_type", "purchase").gte("order_date", from).lte("order_date", to).limit(10000),
    supabase.from("clients").select("id", { count: "exact", head: true }).eq("workspace_id", ws).gte("created_at", from).lte("created_at", `${to}T23:59:59`),
  ]);
  const wiAll = await supabase.from("workshop_invoices").select("id", { count: "exact", head: true }).eq("workspace_id", ws).gte("issue_date", from).lte("issue_date", to);
  const posRevenue = sum(pos.data, (r) => Number(r.total));
  const workshopRevenue =
    sum(wi.data, (r) => Number(r.total)) +
    sum(
      (wo.data || []).filter((r: any) => !(r.workshop_invoices || []).some((x: any) => x.status === "paid")),
      (r) => Number(r.advance_cost || 0) + Number(r.remaining_cost || 0),
    );
  const expenses = sum(exp.data, (r) => Number(r.total_amount)) + sum(po.data, (r) => Number(r.total ?? r.subtotal));
  // Conversión = facturas emitidas cobradas / facturas emitidas (venta + reparación) del periodo
  const paidCount = (pos.data?.length || 0) + (wi.data?.length || 0);
  const issuedCount = (posAll.count || 0) + (wiAll.count || 0);
  const conversion = issuedCount ? (paidCount / issuedCount) * 100 : 0;
  return { posRevenue, workshopRevenue, revenue: posRevenue + workshopRevenue, expenses, conversion, newClients: cl.count || 0 };
}

export function useDashboardKPIs(range: KPIRange) {
  const [kpis, setKpis] = useState<KPIData>({
    totalClients: 0, clientsChange: 0, revenue: 0, revenueChange: 0, posRevenue: 0, posChange: 0,
    workshopRevenue: 0, workshopChange: 0, expenses: 0, expensesChange: 0, activeWorkOrders: 0,
    conversionRate: 0, conversionChange: 0, loading: true,
  });
  const { workspace } = useWorkspace();
  const ws = workspace?.id || null;
  const fromKey = d(range.from), toKey = d(range.to);

  useEffect(() => {
    if (!ws) return;
    let cancelled = false;
    setKpis((p) => ({ ...p, loading: true }));
    (async () => {
      try {
        const len = range.to.getTime() - range.from.getTime();
        const prevTo = new Date(range.from.getTime() - 86400000);
        const prevFrom = new Date(prevTo.getTime() - len);
        const [cur, prev, clients, active] = await Promise.all([
          periodTotals(ws, fromKey, toKey),
          periodTotals(ws, d(prevFrom), d(prevTo)),
          supabase.from("clients").select("id", { count: "exact", head: true }).eq("workspace_id", ws).eq("status", "active"),
          supabase.from("work_orders").select("id", { count: "exact", head: true }).eq("workspace_id", ws).in("status", ["pending", "in_progress", "waiting_parts"]),
        ]);
        if (cancelled) return;
        setKpis({
          totalClients: clients.count || 0,
          clientsChange: pct(cur.newClients, prev.newClients),
          revenue: cur.revenue, revenueChange: pct(cur.revenue, prev.revenue),
          posRevenue: cur.posRevenue, posChange: pct(cur.posRevenue, prev.posRevenue),
          workshopRevenue: cur.workshopRevenue, workshopChange: pct(cur.workshopRevenue, prev.workshopRevenue),
          expenses: cur.expenses, expensesChange: pct(cur.expenses, prev.expenses),
          activeWorkOrders: active.count || 0,
          conversionRate: cur.conversion, conversionChange: cur.conversion - prev.conversion,
          loading: false,
        });
      } catch (e) {
        logger.error("Error fetching KPIs:", e);
        if (!cancelled) setKpis((p) => ({ ...p, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [ws, fromKey, toKey]);

  return kpis;
}
